import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _extends from "@babel/runtime/helpers/extends";
import get from 'lodash/get';
import { DateUtil, CommonUtil, FolderUtil } from '../utils';
import { FOLDER_TYPE } from '../utils/consts';
var getId = function getId(folder) {
  return folder.id || folder.uuid || CommonUtil.folderUrn2Guid(folder.urn);
};
var mapTitle = function mapTitle(folder) {
  return _extends({}, folder, {
    title: FolderUtil.getFolderTitle(folder)
  });
};

// Folder v3 API won't return the isRoot future add a specific logic here.
var isRootFolder = function isRootFolder(_ref) {
  var isRoot = _ref.isRoot,
    urn = _ref.urn,
    path = _ref.path;
  if (typeof isRoot === 'boolean') return isRoot;
  if (!path) return false;
  try {
    var _match;
    var pathUrn = (_match = (urn || '').match(/fs\.folder:co\.(.*)/)) === null || _match === void 0 ? void 0 : _match[1];
    if (pathUrn && path.startsWith(pathUrn)) return true;
    return false;
  } catch (e) {
    return false;
  }
};
var getFolderType = function getFolderType(acsFolderType, folderType) {
  if (acsFolderType) return acsFolderType.toLowerCase();
  return (folderType || '').toLowerCase();
};

// Remap short urn path to full urn path
var toFullUrnPath = function toFullUrnPath(folderUrn, path) {
  if (!folderUrn || !path || !folderUrn.includes('fs.folder:co.')) return path;

  // E.g. urn:adsk.wipqa:fs.folder:co.HU-H9TBkQQeNrhVCY7epaA
  var index = folderUrn.indexOf('fs.folder:co.') + 'fs.folder:co.'.length;
  var prefix = folderUrn.substring(0, index);
  var urns = path.split('/');
  var isShortUrn = urns.some(function (urn) {
    return /^[a-zA-Z0-9-_]{22}$/.test(urn);
  });
  return !isShortUrn ? path : urns.map(function (urn) {
    return "".concat(prefix).concat(urn);
  }).join('/');
};

/**
 * [Model] Configuration
 */
var Configuration = /*#__PURE__*/function () {
  function Configuration() {
    _classCallCheck(this, Configuration);
  }
  return _createClass(Configuration, null, [{
    key: "parseV1",
    value:
    /**
     * Parse snake case data
     */
    function parseV1(rowData) {
      var data = rowData || {};
      var config = new Configuration();
      config.actions = data.actions;
      config.allowedTypes = data.allowed_types;
      config.visibleTypes = data.visible_types;
      return config;
    }
  }]);
}();
/**
 * [Model] Folder Items
 */
var FolderItem = /*#__PURE__*/function () {
  function FolderItem() {
    _classCallCheck(this, FolderItem);
  }
  return _createClass(FolderItem, null, [{
    key: "parseV3",
    value:
    /**
     * V3 api entity parse
     */
    function parseV3(rowData) {
      var data = rowData || {};
      var entity = new FolderItem();
      entity.configurations = data.configurations;
      entity.deleted = !!data.deleted;
      entity.folderType = getFolderType(CommonUtil.getValue(data, 'configurations.acsFolderType'), data.folderType);
      entity.hasSubfolders = data.hasSubfolders;
      entity.hidden = data.hidden;
      entity.id = getId(data);
      entity.isRoot = isRootFolder(data);
      entity.isSystemFolder = data.ownerType === 'SYSTEM';
      entity.lastModifiedTime = DateUtil.dateStringReformat(data.updatedAt);
      entity.lastModifiedUserId = data.updatedBy;
      entity.lastModifiedUserName = data.updatedByName;
      entity.options = data.options;
      entity.parentId = CommonUtil.folderUrn2Guid(data.parentUrn);
      entity.parentUrn = data.parentUrn;
      entity.permissionActions = data.permissionActions;
      entity.title = data.name;
      entity.trashed = data.trashed;
      entity.urn = data.urn;
      entity.description = data.description;

      // Naming standard related fields
      entity.namingStandards = data.namingStandards || [];
      entity.hasNS = entity.namingStandards.length > 0;
      entity.folders = (data.folders || []).map(function (folder) {
        return FolderItem.parseV3(folder);
      });
      entity.parents = (data.parents || []).map(function (folder) {
        return FolderItem.parseParentsV3(folder, entity);
      });
      // CP Search API
      entity.highlights = data.highlights;

      // Append folder related path info
      var updated = mapTitle(entity);
      updated.displayPath = data.displayPath ? FolderUtil.parseFolderDisplayPath(_extends({}, updated, {
        displayPath: data.displayPath
      })) : [].concat(_toConsumableArray(updated.parents.map(function (_ref2) {
        var title = _ref2.title;
        return title;
      })), [updated.title]).join('/');
      // Setup urn path with full folder urn and generate from parents first
      updated.path = updated.parents.length > 0 ? [].concat(_toConsumableArray(updated.parents.map(function (_ref3) {
        var urn = _ref3.urn;
        return urn;
      })), [updated.urn]).join('/') : toFullUrnPath(data.urn, data.path);
      return updated;
    }

    /**
     * Parse parents
     */
  }, {
    key: "parseParentsV3",
    value: function parseParentsV3(data, child) {
      var rowData = data ? data : {};
      var entity = new FolderItem();
      entity.hidden = rowData.hidden;
      entity.id = getId(rowData);
      entity.isRoot = isRootFolder(rowData);
      entity.isSystemFolder = rowData.ownerType === 'SYSTEM';
      entity.parentId = CommonUtil.folderUrn2Guid(rowData.parentUrn);
      entity.parentUrn = rowData.parentUrn;
      entity.readonly = true;
      entity.title = rowData.title || rowData.name;
      entity.trashed = rowData.trashed;
      entity.urn = rowData.urn;
      entity.description = rowData.description;

      // Use same config as child
      entity.configurations = child.configurations;
      entity.folderType = child.folderType;
      return mapTitle(entity);
    }

    /**
     * Parse snake case data
     */
  }, {
    key: "parseV1",
    value: function parseV1(rowData) {
      var data = rowData || {};
      var entity = new FolderItem();
      entity.configurations = Configuration.parseV1(data.configurations);
      entity.deleted = data.deleted;
      entity.folderType = getFolderType(CommonUtil.getValue(data, 'configurations.acs_folder_type'), data.folder_type);
      entity.hasSubfolders = data.has_subfolders;
      entity.hidden = data.hidden;
      entity.id = getId(data);
      entity.isRoot = data.is_root;
      entity.isSystemFolder = data.is_system_folder;
      entity.lastModifiedTime = DateUtil.dateStringReformat(data.last_modified_time);
      entity.lastModifiedUserId = data.last_modified_user_id;
      entity.lastModifiedUserName = data.last_modified_user_name;
      entity.options = _extends({
        viewOption: data.view_option
      }, data.user_preferences); // to compatible with V3
      entity.parentId = CommonUtil.folderUrn2Guid(data.parent_urn);
      entity.parentUrn = data.parent_urn;
      entity.permissionActions = data.permission_actions;
      entity.title = data.title;
      entity.urn = data.urn;
      entity.description = data.description;

      // Naming standard related fields
      entity.namingStandards = data.naming_standards || [];
      entity.hasNS = entity.namingStandards.length > 0;
      entity.folders = (data.folders || []).map(function (folder) {
        return FolderItem.parseV1(folder);
      });
      entity.parents = (data.parents || []).map(function (folder) {
        return FolderItem.parseParentV1(folder, entity);
      });
      entity.path = toFullUrnPath(data.urn, data.path);
      return mapTitle(entity);
    }

    /**
     * Parse snake case data
     */
  }, {
    key: "parseParentV1",
    value: function parseParentV1(rowData, child) {
      var entity = new FolderItem();
      entity.id = getId(rowData);
      entity.isRoot = !!rowData.is_root;
      entity.isSystemFolder = rowData.is_system_folder;
      entity.parentId = CommonUtil.folderUrn2Guid(rowData.parent_urn);
      entity.parentUrn = rowData.parent_urn;
      entity.readonly = true;
      entity.title = rowData.title;
      entity.urn = rowData.urn;
      entity.description = rowData.description;

      // Use PROJECT_FILE as folder type here if child is a shared folder to avoid display wrong icon and name for parent folder
      // All use should have the permission to the shared folder so shared folder couldn't be included inside the parents array.
      entity.folderType = child.folderType && [FOLDER_TYPE.PROJECT_FILE, FOLDER_TYPE.SHARED].includes(child.folderType) ? FOLDER_TYPE.PROJECT_FILE : child.folderType;
      entity.configurations = child.configurations;
      return mapTitle(entity);
    }

    /**
     * Parse camel case data
     */
  }, {
    key: "parseParentsV2",
    value: function parseParentsV2() {
      var folders = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      var entity = new FolderItem();

      // Return if no valid folder
      if (folders.length === 0) return entity;
      var folder = folders.pop();
      if (!folder) return entity;
      entity.urn = folder.urn;
      entity.id = getId(folder);
      entity.permissionActions = folder.actions;
      entity.folderType = getFolderType(CommonUtil.getValue(folder, 'configurations.acsFolderType', folder.folderType));
      entity.isRoot = folder.isRoot;
      entity.options = {
        viewOption: get(folder, 'options.view')
      }; // To keep compact with V1 API, we only care the view options
      entity.configurations = folder.configurations || {};
      entity.lastModifiedTime = folder.updatedAt;
      entity.isSystemFolder = folder.isSystemFolder;
      entity.description = folder.description;

      // Naming standard related fields
      entity.namingStandards = folder.namingStandards || [];
      entity.hasNS = entity.namingStandards.length > 0;

      // Localization folder name (top level) to 'Documents'
      entity.title = FolderUtil.getFolderTitle(_extends({}, folder, {
        folderType: getFolderType(CommonUtil.getValue(folder, 'configurations.acsFolderType', folder.folderType))
      }));
      var paths = folders.reduce(function (preValue, curFolder) {
        preValue.push({
          urn: curFolder.urn,
          title: FolderUtil.getFolderTitle(_extends({}, curFolder, {
            folderType: getFolderType(CommonUtil.getValue(curFolder, 'configurations.acsFolderType', curFolder.folderType))
          }))
        });
        return preValue;
      }, []);
      paths.push({
        title: entity.title,
        urn: entity.urn
      });

      // Setup the path with folder title
      entity.displayPath = paths.map(function (path) {
        return path.title;
      }).join('/');
      entity.path = paths.map(function (path) {
        return path.urn;
      }).join('/');
      return entity;
    }
  }]);
}();
export { FolderItem as default };